import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import { getUserRoleFromToken } from "../utils/getUserRoleFromToken";
import TruncateWithTooltip from "../components/TruncateWithTooltip";

registerLocale("en-GB", enGB);

const API = "/api";

// ---------- auth/ui context
const token = localStorage.getItem("token");
const userRole = getUserRoleFromToken(token);
const isAdmin = userRole === "admin";

// ---------- helpers
const pad2 = (n) => String(n).padStart(2, "0");
const formatLocalYMD = (d) =>
  d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : undefined;
const addDays = (d, n) =>
  d ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + n) : undefined;

function getMyUserId() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    const payload = JSON.parse(atob((t.split(".")[1] || "").replace(/-/g, "+").replace(/_/g, "/")));
    return (
      payload?.id ??
      payload?.user_id ??
      payload?.userId ??
      payload?.user?.id ??
      payload?.sub ??
      null
    );
  } catch {
    return null;
  }
}
const myUserId = getMyUserId();

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}
function durationMs(start, end) {
  if (!start || !end) return null;
  return new Date(end).getTime() - new Date(start).getTime();
}
function formatDuration(start, end) {
  const ms = durationMs(start, end);
  if (ms == null) return "-";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

// ---------- component
function TimeWorkPage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(() => window.localStorage.getItem("tw_tab") || "time");
  useEffect(() => {
    window.localStorage.setItem("tw_tab", tab);
  }, [tab]);

  const [dateRange, setDateRange] = useState([null, null]);

  // data
  const [timeEntries, setTimeEntries] = useState([]);
  const [workEntries, setWorkEntries] = useState([]);

  // loading/errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // sort state
  const [timeSort, setTimeSort] = useState({ col: "clock_in", order: "desc" });
  const [workSort, setWorkSort] = useState({ col: "start_time", order: "desc" });

  // fetchers (force personal scope, even if admin)
  const fetchTime = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/time`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          from: formatLocalYMD(dateRange[0]),
          to: formatLocalYMD(addDays(dateRange[1], 1)),
          user: myUserId, // ← personal scope
        },
      });
      setTimeEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Could not fetch time entries.");
      setTimeEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWork = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          from: formatLocalYMD(dateRange[0]),
          to: formatLocalYMD(addDays(dateRange[1], 1)),
          user: myUserId, // ← personal scope
        },
      });
      setWorkEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Could not fetch work entries.");
      setWorkEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // initial + on tab/range change
  useEffect(() => {
    if (tab === "time") fetchTime();
    else fetchWork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dateRange]);

  // sorting helpers
  const setTimeSortCol = (col) =>
    setTimeSort((s) => ({ col, order: s.col === col ? (s.order === "asc" ? "desc" : "asc") : "asc" }));
  const setWorkSortCol = (col) =>
    setWorkSort((s) => ({ col, order: s.col === col ? (s.order === "asc" ? "desc" : "asc") : "asc" }));

  const headerClass = (activeCol, col) =>
    `py-2 text-left font-semibold cursor-pointer select-none ${activeCol === col ? "text-blue-700" : ""}`;
  const arrow = (activeCol, order, col) => (activeCol === col ? (order === "asc" ? "▲" : "▼") : "");

  // ---------- sorted data (no "user" sort on personal page)
  const sortedTimeEntries = useMemo(() => {
    const active = timeEntries.filter((e) => !e.clock_out);
    const ended = timeEntries.filter((e) => !!e.clock_out);
    const cmp = (a, b) => {
      let ax = 0,
        bx = 0;
      if (timeSort.col === "clock_in" || timeSort.col === "clock_out") {
        ax = a[timeSort.col] ? new Date(a[timeSort.col]).getTime() : 0;
        bx = b[timeSort.col] ? new Date(b[timeSort.col]).getTime() : 0;
      } else if (timeSort.col === "duration") {
        ax = durationMs(a.clock_in, a.clock_out) ?? 0;
        bx = durationMs(b.clock_in, b.clock_out) ?? 0;
      }
      return timeSort.order === "asc" ? ax - bx : bx - ax;
    };
    return [...active.sort(cmp), ...ended.sort(cmp)];
  }, [timeEntries, timeSort]);

  const sortedWorkEntries = useMemo(() => {
    const active = workEntries.filter((e) => !e.end_time);
    const ended = workEntries.filter((e) => !!e.end_time);
    const cmp = (a, b) => {
      let ax = 0,
        bx = 0;
      if (workSort.col === "start_time" || workSort.col === "end_time") {
        ax = a[workSort.col] ? new Date(a[workSort.col]).getTime() : 0;
        bx = b[workSort.col] ? new Date(b[workSort.col]).getTime() : 0;
      } else if (workSort.col === "duration") {
        ax = durationMs(a.start_time, a.end_time) ?? 0;
        bx = durationMs(b.start_time, b.end_time) ?? 0;
      }
      return workSort.order === "asc" ? ax - bx : bx - ax;
    };
    return [...active.sort(cmp), ...ended.sort(cmp)];
  }, [workEntries, workSort]);

  // ---------- navigation to Projects (ownership-aware)
  const navigateToRowTarget = async (w) => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const ownerFromProject = (pd) =>
      pd?.user_id ?? pd?.owner_id ?? pd?.ownerId ?? pd?.owner?.id ?? pd?.user?.id ?? null;

    let ownerId = null;
    let projectIdToOpen = null;
    let fileId = null;
    let folderId = null;
    let foreign = false;

    try {
      if (w.file_id) {
        fileId = w.file_id;
        try {
          const fRes = await axios.get(`${API}/files/${w.file_id}`, auth);
          const projId = fRes.data?.project_id ?? null;
          projectIdToOpen = projId ?? w.project_id ?? w.project_parent_id ?? w.parent_id ?? null;

          if (projId != null) {
            try {
              const pRes = await axios.get(`${API}/projects/${projId}`, auth);
              ownerId = ownerFromProject(pRes.data);
            } catch (e) {
              if ([403, 404].includes(e?.response?.status)) foreign = true;
            }
          }
        } catch (e) {
          if ([403, 404].includes(e?.response?.status)) foreign = true;
        }
      } else if (w.project_id) {
        folderId = w.project_id;
        try {
          const pRes = await axios.get(`${API}/projects/${w.project_id}`, auth);
          ownerId = ownerFromProject(pRes.data);
          projectIdToOpen = pRes.data?.parent_id ?? w.parent_id ?? null;
        } catch (e) {
          if ([403, 404].includes(e?.response?.status)) foreign = true;
          projectIdToOpen = w.parent_id ?? null;
        }
      } else {
        projectIdToOpen = w.parent_id ?? null;
      }
    } catch {
      /* ignore; use defaults */
    }

    if (ownerId == null && w.user_id != null) ownerId = w.user_id;
    if (!foreign && ownerId != null && myUserId != null) {
      foreign = String(ownerId) !== String(myUserId);
    }

    navigate(foreign ? "/admin/workers" : "/projects", {
      state: {
        ownerId: foreign ? ownerId : undefined,
        projectId: projectIdToOpen ?? null,
        fileId,
        folderId,
      },
    });
  };

  // lock widths (match AdminWorkersPage)
  const COL_TIME = "16rem";
  const COL_DURATION = "8rem";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isAdmin={isAdmin} />

      <div className="pt-20 max-w-6xl mx-auto px-4">
        <h2 className="text-2xl font-bold mb-6">My Time & Work</h2>

        <div className="flex gap-6 mb-6 border-b pb-2">
          {["time", "work"].map((k) => (
            <button
              key={k}
              className={`text-lg font-semibold px-4 py-2 border-b-2 ${
                tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500"
              }`}
              onClick={() => setTab(k)}
            >
              {k === "time" ? "Time Entries" : "Work Entries"}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-4 items-end mb-4">
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Date Range</label>
            <DatePicker
              selectsRange
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(dates) => setDateRange(dates)}
              isClearable
              placeholderText="Pick date or range"
              className="border border-gray-300 rounded p-2 bg-gray-100 w-60"
              locale="en-GB"
              dateFormat="dd-MM-yyyy"
            />
          </div>
        </div>

        {/* TIME TABLE (no User column here) */}
        {tab === "time" && (
          <div className="bg-white rounded-lg shadow border">
            <table className="w-full">
              <colgroup>
                <col style={{ width: COL_TIME }} />
                <col style={{ width: COL_TIME }} />
                <col style={{ width: COL_DURATION }} />
              </colgroup>
              <thead className="bg-gray-100 border-b">
                <tr className="text-gray-600">
                  <th
                    className={`py-2 pl-4 ${headerClass(timeSort.col, "clock_in")}`}
                    onClick={() => setTimeSortCol("clock_in")}
                  >
                    Clock In {arrow(timeSort.col, timeSort.order, "clock_in")}
                  </th>
                  <th
                    className={headerClass(timeSort.col, "clock_out")}
                    onClick={() => setTimeSortCol("clock_out")}
                  >
                    Clock Out {arrow(timeSort.col, timeSort.order, "clock_out")}
                  </th>
                  <th
                    className={headerClass(timeSort.col, "duration")}
                    onClick={() => setTimeSortCol("duration")}
                  >
                    Duration {arrow(timeSort.col, timeSort.order, "duration")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-red-600">
                      {error}
                    </td>
                  </tr>
                ) : sortedTimeEntries.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-400">
                      No time entries found.
                    </td>
                  </tr>
                ) : (
                  sortedTimeEntries.map((row) => (
                    <tr key={row.id} className="border-b last:border-b-0">
                      <td className="py-2 pl-4">{formatDateTime(row.clock_in)}</td>
                      <td className="py-2">
                        {row.clock_out ? (
                          formatDateTime(row.clock_out)
                        ) : (
                          <span className="text-green-700 font-semibold">Active</span>
                        )}
                      </td>
                      <td className="py-2">{formatDuration(row.clock_in, row.clock_out)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* WORK TABLE (no User column here) */}
        {tab === "work" && (
          <div className="bg-white rounded-lg shadow border">
            <table className="w-full">
              <colgroup>
                <col style={{ width: COL_TIME }} />
                <col style={{ width: COL_TIME }} />
                <col /> {/* Project/File flex */}
                <col style={{ width: COL_DURATION }} />
              </colgroup>
              <thead className="bg-gray-100 border-b">
                <tr className="text-gray-600">
                  <th
                    className={`py-2 pl-4 ${headerClass(workSort.col, "start_time")}`}
                    onClick={() => setWorkSortCol("start_time")}
                  >
                    Start {arrow(workSort.col, workSort.order, "start_time")}
                  </th>
                  <th
                    className={headerClass(workSort.col, "end_time")}
                    onClick={() => setWorkSortCol("end_time")}
                  >
                    End {arrow(workSort.col, workSort.order, "end_time")}
                  </th>
                  <th className="py-2 text-left font-semibold">Project / File</th>
                  <th
                    className={headerClass(workSort.col, "duration")}
                    onClick={() => setWorkSortCol("duration")}
                  >
                    Duration {arrow(workSort.col, workSort.order, "duration")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-red-600">
                      {error}
                    </td>
                  </tr>
                ) : sortedWorkEntries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-400">
                      No work entries found.
                    </td>
                  </tr>
                ) : (
                  sortedWorkEntries.map((w) => (
                    <tr key={w.id} className="border-b last:border-b-0">
                      <td className="py-2 pl-4">{formatDateTime(w.start_time)}</td>
                      <td className="py-2">
                        {w.end_time ? (
                          formatDateTime(w.end_time)
                        ) : (
                          <span className="text-green-700 font-semibold">Active</span>
                        )}
                      </td>
                      <td className="py-2">
                        {w.file_id && w.file_name ? (
                          <button
                            type="button"
                            onClick={() => navigateToRowTarget(w)}
                            className="inline-flex items-center gap-2 text-blue-700 underline cursor-pointer focus:outline-none"
                          >
                            <span aria-hidden="true">📄</span>
                            <TruncateWithTooltip text={w.file_name} />
                          </button>
                        ) : w.project_id && w.project_name ? (
                          <button
                            type="button"
                            onClick={() => navigateToRowTarget(w)}
                            className="inline-flex items-center gap-2 text-blue-700 underline cursor-pointer focus:outline-none"
                          >
                            <span aria-hidden="true">📁</span>
                            <TruncateWithTooltip text={w.project_name} />
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-2">{formatDuration(w.start_time, w.end_time)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimeWorkPage;
